import { lazy } from 'react';
import { isChunkLoadError, reloadPageForStaleChunks } from './chunkLoadError';

/**
 * Enhanced lazy loading with retry logic for ChunkLoadError
 *
 * This wrapper adds retry functionality to React.lazy() to handle chunk load failures
 * that occur due to cache mismatches (e.g., when main.js is cached but chunks are updated).
 *
 * @param {Function} componentImport - Function that returns a dynamic import promise
 * @param {number} retries - Number of retry attempts (default: 2)
 * @returns {React.LazyExoticComponent} Lazy-loaded component with retry logic
 *
 * @example
 * const MyComponent = lazyWithRetry(() => import('./MyComponent'));
 */
export const lazyWithRetry = (componentImport, retries = 2) => {
  return lazy(() => {
    return new Promise((resolve, reject) => {
      const attemptImport = (attemptsLeft) => {
        componentImport()
          .then(resolve)
          .catch((error) => {
            if (isChunkLoadError(error) && attemptsLeft > 0) {
              console.warn(`[ChunkLoadError] Retrying chunk load (${(retries + 1) - attemptsLeft}/${retries + 1})...`);
              setTimeout(() => {
                attemptImport(attemptsLeft - 1);
              }, 500);
              return;
            }

            if (isChunkLoadError(error)) {
              console.warn('[ChunkLoadError] Retries exhausted. Reloading page to fetch latest chunks...');
              const reloaded = reloadPageForStaleChunks();
              if (reloaded) {
                return;
              }
            }

            reject(error);
          });
      };

      attemptImport(retries);
    });
  });
};

export default lazyWithRetry;
